// Shared bits of the feedback UI: 44px touch targets in the app's button styles, the status chip.
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { STATUS_LABEL, type PinStatus } from "../../lib/feedback/api";
import { cx } from "../ui";

type Variant = "primary" | "secondary" | "ghost";

/** The app's Button look at a 44px touch height. */
export const FbButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }>(function FbButton(
  { variant = "secondary", className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        "focus-ring inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-md px-4 text-[14px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        variant === "primary" && "bg-primary text-on-primary hover:bg-primary-hover",
        variant === "secondary" && "border border-line bg-surface text-ink hover:bg-hover",
        variant === "ghost" && "text-ink-2 hover:bg-hover hover:text-ink",
        className,
      )}
      {...rest}
    />
  );
});

const TONE: Record<PinStatus, string> = {
  open: "border-info/40 bg-info-bg text-info",
  in_progress: "border-warning/40 bg-warning-bg text-warning",
  fixed: "border-success/40 bg-success-bg text-success",
  closed: "border-line bg-sunken text-ink-3",
};

export function StatusChip({ status }: { status: PinStatus }) {
  return (
    <span className={cx("inline-flex h-[22px] items-center rounded-full border px-2 text-[12px] font-semibold whitespace-nowrap", TONE[status])}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/** The "Preview" chip on gated nav items and screens. */
export function PreviewChip({ onDark = false }: { onDark?: boolean }) {
  return (
    <span
      className={cx(
        "ml-1.5 inline-flex h-[18px] items-center rounded-full px-1.5 text-[10px] font-bold tracking-[0.04em] uppercase",
        onDark ? "bg-white/15 text-white" : "bg-info-bg text-info",
      )}
    >
      Preview
    </span>
  );
}

export function when(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
